import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { Socket, io } from 'socket.io-client';
import { environment } from '@/environments/environment';
import { ConnectionStatus } from '../core/service';
import { SynPayload } from '../core/types';

const NO_SESSION = undefined;

@Injectable()
export class WebsocketApiService implements OnDestroy {
  private onConnectionEvt = new BehaviorSubject<ConnectionStatus>(
    'disconnected'
  );

  private sessionId: string | undefined = NO_SESSION;

  private socket?: Socket;

  private subscription = new Subscription();

  private currentListeners: Record<string, (...args: unknown[]) => void> = {};

  ngOnDestroy() {
    this.disconnect();
    this.subscription.unsubscribe();
  }

  connect() {
    if (this.onConnectionEvt.value !== 'disconnected') {
      return;
    }

    if (!this.socket) {
      this.bindSocket();
      this.connect();
      return;
    }

    this.socket.on('disconnect', () => {
      if (this.sessionId) {
        this.reconnect();
        return;
      }

      this.onConnectionEvt.next('disconnected');
    });

    this.socket.on('socket:syn-ack', this.receiveSynAck.bind(this));
    this.socket.on('socket:fin-ack', this.receiveFinAck.bind(this));

    this.onConnectionEvt.next('connecting');

    connectSocket(this.socket).subscribe({
      next: () => this.sendSyn(),
      error: () => this.reconnect(),
    });
  }

  disconnect() {
    this.onConnectionEvt.next('disconnected');
    this.sessionId = NO_SESSION;
    this.currentListeners = {};
    this.socket?.removeAllListeners();
    this.socket?.emit('socket:fin');
    delete this.socket;
  }

  emit(topic: string, data: unknown) {
    if (!this.socket || this.onConnectionEvt.value !== 'connected') {
      throw new Error('Unconnected from server');
    }

    this.socket.emit(topic, data);
  }

  addListener(topic: string, listener: (...args: unknown[]) => void) {
    this.removeListener(topic);
    this.currentListeners[topic] = listener;

    if (this.socket && this.onConnectionEvt.value === 'connected') {
      this.socket.on(topic, listener);
    }
  }

  removeListener(topic: string) {
    const listener = this.currentListeners[topic];
    if (!listener) {
      return;
    }

    this.socket?.removeListener(topic, listener);
    delete this.currentListeners[topic];
  }

  get connectionStatus() {
    return this.onConnectionEvt.asObservable();
  }

  private reconnect() {
    const socket = this.socket;
    if (!socket) {
      return;
    }

    const maxAttempts = 5;
    const maxDelay = 5000;
    const initialDelay = 1000;
    const delayFactor = 0.5;
    let delay = initialDelay;
    let attempt = 0;

    this.onConnectionEvt.next('connecting');

    const call = () => {
      attempt++;
      delay = parseInt(
        Math.min(
          delay + delay * delayFactor * Math.random(),
          maxDelay
        ).toString()
      );

      connectSocket(socket).subscribe({
        next: () => this.sendSyn(),
        error: () => {
          if (attempt >= maxAttempts) {
            this.onConnectionEvt.next('disconnected');
            return;
          }

          setTimeout(call, delay);
        },
      });
    };

    call();
  }

  private sendSyn() {
    const previousSessionId = this.sessionId;
    this.sessionId = NO_SESSION;
    const token = localStorage.getItem('token');
    this.socket?.emit('socket:syn', { sessionId: previousSessionId, token });
  }

  private receiveSynAck(data: SynPayload) {
    this.sessionId = data.sessionId;

    if (!this.socket) {
      throw new Error('Socket undefined');
    }

    this.socket.emit('socket:ack');
    this.onConnectionEvt.next('connected');
    this.restoreListeners();
  }

  private receiveFinAck() {
    this.socket?.disconnect();
    delete this.socket;
  }

  private restoreListeners() {
    if (!this.socket) {
      return;
    }

    Object.entries(this.currentListeners).forEach(([topic, listener]) => {
      this.socket!.off(topic, listener);
      this.socket!.on(topic, listener);
    });
  }

  private bindSocket() {
    const token = localStorage.getItem('token');

    this.socket = io(environment.apiHost, {
      path: '/sockets/',
      autoConnect: false,
      reconnection: false,
      transports: ['websocket'],
      withCredentials: true,
      extraHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }
}

function connectSocket(socket: Socket) {
  const connection = new Subject<void>();

  const onConnectHandler = () => {
    connection.next();
    onComplete();
  };

  const onConnectErrorHandler = (error: Error) => {
    connection.error(error);
    onComplete();
  };

  const onComplete = () => {
    socket.removeListener('connect', onConnectHandler);
    socket.removeListener('connect_error', onConnectErrorHandler);
    connection.complete();
  };

  socket.on('connect', onConnectHandler);
  socket.on('connect_error', onConnectErrorHandler);
  socket.connect();

  return connection;
}
